import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import { Button, Dropdown, Input, Option } from "@fluentui/react-components";
import { Spinner, SpinnerSize } from "@fluentui/react";

import Pagination from "../../components/pagination/Pagination";
import TableComponent from "../../components/table/TableComponent";
import { AdminService, TransactionReport } from "../../KulcsUzletSvc";
import { useStylesMainPage } from "../../styles/MainPageStyle";
import { formatDate } from "../../utils/HelperFunctions";
import { TABLE } from "../../utils/Enums";

const SEARCH_DELAY_MS = 1000;
const MIN_SEARCH_LENGTH = 3;

export default function TransactionsPage() {
  const styles = useStylesMainPage();
  const [data, setData] = useState<null | TransactionReport[]>();
  const [totalCount, setTotalCount] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0];
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  )
    .toISOString()
    .split("T")[0];

  const [dateFilter, setDateFilter] = useState({
    startDate: firstDayOfMonth,
    endDate: today,
  });

  const [pageSize, setPageSize] = useState("50");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const navigateBack = () => {
    navigate(-1);
  };

  // ------------------------------------------------------------------
  // 1. EFFECT HOOK A DEBOUNCINGHOZ (1 mp késleltetés)
  // ------------------------------------------------------------------
  useEffect(() => {
    // Csak a gépelés után 1 másodperccel állítja be a debouncedSearch állapotot
    const handler = setTimeout(() => {
      if (search.length === 0 || search.length >= MIN_SEARCH_LENGTH) {
        setDebouncedSearch(search);
      }
    }, SEARCH_DELAY_MS);

    // Cleanup függvény: törli az előző időzítőt, ha a felhasználó gépel
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const fetchData = async () => {
    if (
      debouncedSearch.length > 0 &&
      debouncedSearch.length < MIN_SEARCH_LENGTH
    ) {
      // console.log("Keresés megszakítva: kevesebb, mint 3 karakter.");
      return;
    }

    if (!dateFilter.startDate || !dateFilter.endDate) {
      console.error("Hiányzó dátumszűrő. API hívás megszakítva.");
      return;
    }

    setIsDataLoading(true);
    try {
      const size = Number(pageSize);
      const skipValue = (currentPage - 1) * size;

      const res = await AdminService.getAdminTransactionsReport(
        debouncedSearch,
        dateFilter.startDate,
        dateFilter.endDate,
        undefined,
        skipValue,
        size,
        undefined,
      );

      setData(res.items ?? []);
      setTotalCount(res.totalCount ?? 0);
    } catch (e) {
      console.error("Adatlekérési hiba:", e);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, debouncedSearch, pageSize, currentPage]);

  const exportList = async () => {
    try {
      // Az aktuális oldal adatainak exportálása
      const dataToExport = data ?? [];

      if (dataToExport.length === 0) {
        alert("Nincsenek exportálandó adatok az aktuális oldalon");
        return;
      }

      const headers = [
        "Partner kód",
        "Regisztrációs email",
        "Státusz",
        "Tranzakció id",
        "Tranzakció státusz",
        "Tranzakció dátum",
        "Aktív eddig",
        "Összeg",
        "Kedvezmény (%)",
        "RC",
      ];

      const rows = dataToExport.map((item) => [
        item.customerCode ?? "",
        item.paymentUserEmail ?? "",
        item.state ?? "",
        item.transactionId ?? "",
        item.transactionState ?? "",
        formatDate(item.rowVersion),
        formatDate(item.expiredDate),
        item.amount ?? "",
        item.discount ?? "",
        item.responseCode ?? "",
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");
      const fileName = `transactions_page_${currentPage}_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error("Export failed", e);
    }
  };

  return (
    <div className={styles.main}>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          fontSize: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Tranzakciók
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: "12px" }}>
          <Button onClick={navigateBack}>Vissza</Button>
          <Dropdown
            defaultValue={"50"}
            style={{ minWidth: "80px" }}
            onOptionSelect={(_, data) => {
              setPageSize(data.optionValue as string);
              setCurrentPage(1);
            }}
          >
            <Option value="10">10</Option>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
            <Option value="100">100</Option>
          </Dropdown>
          <span style={{ fontSize: "14px" }}>találat oldalaként</span>
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "flex-end" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "14px",
            }}
          >
            <label>Kezdeti dátum:</label>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, startDate: e.target.value });
                setCurrentPage(1);
              }}
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "6px",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              fontSize: "14px",
            }}
          >
            <label>Záró dátum:</label>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => {
                setDateFilter({ ...dateFilter, endDate: e.target.value });
                setCurrentPage(1);
              }}
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "6px",
              }}
            />
          </div>

          <Input
            type="text"
            placeholder={`Keresés (min. ${MIN_SEARCH_LENGTH} karakter)...`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: "200px" }}
          />
          <Button onClick={exportList}>Excel</Button>
        </div>
      </div>

      {isDataLoading ? (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spinner size={SpinnerSize.large} label="Betöltés..." />
        </div>
      ) : (
        <>
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            pageSize={Number(pageSize)}
            onPageChange={(p) => setCurrentPage(p)}
          />

          <TableComponent
            type={TABLE.TRANSACTIONS}
            data={data}
            isLoading={isDataLoading}
          />
        </>
      )}
    </div>
  );
}
